import React, { useState, forwardRef, useEffect, useRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { useSelector } from 'react-redux';
import 'react-datepicker/dist/react-datepicker.css';
import '@assets/scss/_datepicker.scss';
import { isExistsDate, toDate, getToday, addDates } from '@common/utils/date';
import format from 'date-fns/format';
import ko from 'date-fns/locale/ko';
import SatSelect from './SatSelect';

const SatDatePicker = props => {
  registerLocale('ko', ko);
  const {
    title = '',
    size = 'middle',
    value = '',
    placeholder = '날짜선택',
    disabled = false,
    filterDate = () => true, // 선택 제외 날짜
    disabledDates = [],
    onChange,
    viewIcon = false,
    noDisableCss = false,
  } = props;

  const { isLockScreen } = useSelector(state => state.commonReducer);

  const datepickerRef = useRef();
  const [viewDate, setViewDate] = useState('');
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [isShow, setIsShow] = useState(false);
  const [pickerValue, setPickerValue] = useState(value);

  useEffect(() => setPickerValue(value), [value]);

  useEffect(() => {
    if (isExistsDate(pickerValue)) {
      setViewDate(format(toDate(pickerValue), 'yyyy.MM.dd'));
    } else {
      setViewDate(getToday());
    }
  }, [pickerValue]);

  useEffect(() => {
    const [min, max] = disabledDates;

    if (min) setMinDate(toDate(min));
    if (max) setMaxDate(toDate(max));
  }, [disabledDates]);

  useEffect(() => {
    if (isLockScreen && isShow) {
      datepickerRef.current.setOpen(false);
    }
  }, [isLockScreen]);

  const CustomInput = forwardRef((props, ref) => {
    const { viewValue, onClick, setIsShow, setViewDate } = props;

    const getDateValue = () => {
      return isExistsDate(viewValue)
        ? format(toDate(viewValue), 'yyyy.MM.dd')
        : placeholder;
    };

    const getStyleClass = () => {
      let className = `input-wrap val box-sel ${
        noDisableCss ? '' : 'disabled'
      }`;

      if (size === 'small') className += ' sel-s';
      if (disabled) className += ' disabled';

      return className;
    };

    const handleClickCalendar = () => {
      if (isExistsDate(viewValue)) {
        setViewDate(format(toDate(viewValue), 'yyyy.MM.dd'));
      } else {
        setViewDate(getToday());
      }

      setIsShow(true);
      onClick();
    };

    return (
      <div className={getStyleClass()} onClick={handleClickCalendar}>
        <div className="input-area ">
          {title !== '' && <span className="input-tit">{title}</span>}
          <span className="sel-txt">{getDateValue()}</span>
          {viewIcon && <span className="ico ico-calendar"></span>}
        </div>
      </div>
    );
  });

  const customHeader = props => {
    const todayYear = new Date().getFullYear();
    const { changeYear, decreaseMonth, increaseMonth } = props;
    const [year, month, date] = viewDate.split('.');
    const yearOptions = Array.from({ length: 100 }, (_, i) => {
      const yearValue = todayYear - i;
      return { label: yearValue, value: yearValue };
    });

    const prevButton = () => {
      const newViewDate = addDates(viewDate, 'month', -1);
      setViewDate(newViewDate);
      decreaseMonth();
    };

    const nextButton = () => {
      const newViewDate = addDates(viewDate, 'month', 1);
      setViewDate(newViewDate);
      increaseMonth();
    };

    const selectYear = value => {
      const newViewDate = `${value}.${month}.${date}`;
      const newPickerDate = `${value}${month}${date}`;
      setViewDate(newViewDate);
      setPickerValue(newPickerDate);
      changeYear(value);
    };

    return (
      <>
        <div className="datepicker-nav">
          <button className="btn-nav-pre" onClick={() => prevButton()} />
          <div className="datepicker-current-month">
            <SatSelect
              border
              isPortal={false}
              title={'년도'}
              render={showSelect => (
                <span className="cursor-pointer" onClick={showSelect}>
                  {year}
                </span>
              )}
              value={year}
              options={yearOptions}
              onChange={option => selectYear(option.value)}
            />
            .<span>{month}</span>.<span>{date}</span>
          </div>
          <button className="btn-nav-next" onClick={() => nextButton()} />
        </div>
      </>
    );
  };

  return (
    <>
      <DatePicker
        withPortal
        locale="ko"
        ref={datepickerRef}
        selected={
          $utils.isEmptyString(pickerValue) ? new Date() : toDate(pickerValue)
        }
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        filterDate={filterDate}
        customInput={
          <CustomInput
            viewValue={value}
            setIsShow={value => setIsShow(value)}
            setViewDate={setViewDate}
          />
        }
        renderCustomHeader={customHeader}
        onChange={value => {
          onChange(format(value, 'yyyyMMdd'));
          setIsShow(false);
        }}
        onCalendarClose={() => {}}
      />
    </>
  );
};

export default React.memo(SatDatePicker);
